/**
 * SEO page registry service — config resolution, metadata, inventory queries, linking.
 */
import {
  SEO_PAGE_TYPES,
  SEO_SITE_NAME,
  SEO_DEFAULT_OG_IMAGE,
  SEO_MIN_INVENTORY_TO_INDEX,
  SEO_CITIES,
  SEO_POPULAR_MAKES,
  SEO_BUDGET_RANGES,
} from "../constants/seoPageConstants";
import { M26_MEDELLIN_MODELS, M26_MEDELLIN_BUDGET_SLUGS } from "../data/seoPageContentM26";
import { normalizeSeoSlug } from "../utils/seoSlugHelpers";
import {
  serializeVehicleSearchParams,
  vehicleSearchParamsFromSeoConfig,
} from "../utils/vehicleSearch/vehicleSearchParamsUrl";
import { formatVehicleTitle } from "../utils/automapListing/listingFormatters";

function loadSeoPageRegistry() {
  // Lazy require avoids circular-import init where static bindings stay undefined.
  return require("../data/seoPageRegistry");
}

function isGuideRichPageType(pageType) {
  return pageType === SEO_PAGE_TYPES.GUIDE;
}

export function getSeoPageConfig(path) {
  const normalized = normalizePath(path);
  const index = loadSeoPageRegistry().getSeoPathIndex();
  return index.get(normalized) || null;
}

export function getSeoPagesByType(pageType) {
  return loadSeoPageRegistry().SEO_PAGE_REGISTRY.filter((page) => page.pageType === pageType);
}

export function listIndexableSeoPages({ inventoryCounts = {} } = {}) {
  return loadSeoPageRegistry().SEO_PAGE_REGISTRY.filter((config) => {
    if (config.includeInSitemap === false) return false;
    const count = inventoryCounts[config.path] ?? inventoryCounts[config.id] ?? null;
    return !shouldNoindexSeoPage(config, count);
  });
}

export function normalizePath(path) {
  if (!path) return "/";
  const trimmed = String(path).split("?")[0].replace(/\/+$/, "") || "/";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function resolvePublicCanonicalUrl(config, origin = "") {
  const base = origin.replace(/\/+$/, "");
  const path = config.canonicalUrl || config.path;
  return `${base}${path}`;
}

export function buildSeoPageMetadata(config, { origin = "", inventoryCount = null } = {}) {
  const noindex = shouldNoindexSeoPage(config, inventoryCount);
  const canonicalUrl = resolvePublicCanonicalUrl(config, origin);
  const robots = noindex ? "noindex, follow" : "index, follow";

  return {
    metaTitle: config.metaTitle || `${config.title} | ${SEO_SITE_NAME}`,
    metaDescription: config.metaDescription || config.introCopy,
    canonicalUrl,
    robots,
    robotsDirective: robots,
    openGraph: {
      title: config.metaTitle || config.title,
      description: config.metaDescription || config.introCopy,
      image: SEO_DEFAULT_OG_IMAGE,
      url: canonicalUrl,
      type: "website",
      siteName: SEO_SITE_NAME,
      locale: "es_CO",
    },
    twitter: {
      card: "summary_large_image",
      title: config.metaTitle || config.title,
      description: config.metaDescription || config.introCopy,
      image: SEO_DEFAULT_OG_IMAGE,
    },
  };
}

export function buildSeoPageInventoryQuery(config) {
  const filters = { ...(config.inventoryFilters || {}) };
  const params = {
    page: 1,
    pageSize: 12,
    sort: "best_value_score",
  };

  if (filters.city) params.city = filters.city;
  if (filters.make) params.make = filters.make;
  if (filters.model) params.model = filters.model;
  if (filters.body_type) params.body_type = filters.body_type;
  if (filters.transmission) params.transmission = filters.transmission;
  if (filters.price_min != null) params.price_min = String(filters.price_min);
  if (filters.price_max != null) params.price_max = String(filters.price_max);
  if (filters.search) params.search = filters.search;
  if (config.financingRequired) params.financing = "true";

  return params;
}

export function buildSearchResultsHref(config) {
  const params = vehicleSearchParamsFromSeoConfig(config);
  const sp = serializeVehicleSearchParams(params);
  if (config.financingRequired) {
    sp.set("financing", "true");
  }
  const qs = sp.toString();
  return `/v/automap/vehicles${qs ? `?${qs}` : ""}`;
}

function link(label, href, group) {
  return { label, href, group };
}

export function buildSeoInternalLinks(config) {
  const links = [];
  const citySlug = config.citySlug;

  if (citySlug) {
    loadSeoPageRegistry().getRegistryNearbyCities(citySlug).forEach((cityName) => {
      const nearby = SEO_CITIES.find((c) => c.name === cityName);
      if (nearby) {
        links.push(link(`Carros usados en ${nearby.name}`, `/carros-usados-${nearby.slug}`, "nearby_cities"));
      }
    });
    links.push(link(`Concesionarios en ${config.city}`, `/concesionarios/${citySlug}`, "dealers"));
  }

  if (citySlug === "medellin") {
    links.push(link("Camionetas usadas en Medellín", "/camionetas-usadas-medellin", "body_types"));
    links.push(link("Automóviles usados en Medellín", "/automoviles-usados-medellin", "body_types"));
    links.push(link("Carros financiados en Medellín", "/carros-financiados-medellin", "financing"));
    links.push(link("Carros automáticos en Medellín", "/carros-automaticos-medellin", "transmission"));
    links.push(link("Carros económicos en Medellín", "/carros-economicos-medellin", "budget"));
  } else if (citySlug) {
    links.push(link(`Camionetas usadas en ${config.city}`, `/camionetas-usadas-medellin`, "body_types"));
    links.push(link(`Carros financiados en ${config.city}`, `/carros-financiados-${citySlug}`, "financing"));
  }

  const brandSlugs = config.makeSlug
    ? SEO_POPULAR_MAKES.filter((m) => m.slug !== config.makeSlug).slice(0, 3)
    : SEO_POPULAR_MAKES.slice(0, 4);
  brandSlugs.forEach((make) => {
    const href = citySlug ? `/marcas/${make.slug}/${citySlug}` : `/marcas/${make.slug}/medellin`;
    const label = citySlug ? `${make.name} usados en ${config.city}` : `${make.name} usados en Medellín`;
    links.push(link(label, href, "brands"));
  });

  if (config.makeSlug && citySlug === "medellin") {
    M26_MEDELLIN_MODELS.filter((m) => m.makeSlug === config.makeSlug)
      .slice(0, 2)
      .forEach((model) => {
        links.push(
          link(
            `${model.makeName} ${model.modelName} en Medellín`,
            `/modelos/${model.makeSlug}/${model.modelSlug}/medellin`,
            "models"
          )
        );
      });
  } else if (citySlug === "medellin" && !config.modelSlug) {
    M26_MEDELLIN_MODELS.slice(0, 4).forEach((model) => {
      links.push(
        link(
          `${model.makeName} ${model.modelName} usados`,
          `/modelos/${model.makeSlug}/${model.modelSlug}/medellin`,
          "models"
        )
      );
    });
  }

  M26_MEDELLIN_BUDGET_SLUGS.slice(0, 3).forEach((slug) => {
    const budget = SEO_BUDGET_RANGES.find((b) => b.slug === slug);
    if (!budget) return;
    const href = citySlug ? `/presupuesto/${budget.slug}/${citySlug}` : `/presupuesto/${budget.slug}/medellin`;
    links.push(
      link(
        citySlug ? `Carros ${budget.label.toLowerCase()} en ${config.city}` : `Carros ${budget.label.toLowerCase()} Medellín`,
        href,
        "budget"
      )
    );
  });

  links.push(link("Checklist para comprar carro usado", "/guias/checklist-compra-carro-usado", "guides"));
  links.push(link("Guía de compra segura", "/guias/compra-segura", "guides"));
  links.push(link("Cómo comprar en Medellín", "/guias/como-comprar-carro-usado-en-medellin", "guides"));
  links.push(link("Qué revisar antes de comprar", "/guias/que-revisar-antes-de-comprar-carro-usado", "guides"));

  const deduped = [];
  const seen = new Set();
  links.forEach((entry) => {
    if (entry.href === config.path || seen.has(entry.href)) return;
    seen.add(entry.href);
    deduped.push(entry);
  });

  return deduped;
}

export function buildSeoBreadcrumbs(config) {
  const crumbs = [{ label: "Inicio", href: "/v/automap" }];

  switch (config.pageType) {
    case SEO_PAGE_TYPES.CITY:
      crumbs.push({ label: "Carros usados", href: "/v/automap/vehicles" });
      crumbs.push({ label: config.city, href: config.path });
      break;
    case SEO_PAGE_TYPES.CITY_BODY_TYPE:
    case SEO_PAGE_TYPES.CITY_FINANCING:
    case SEO_PAGE_TYPES.CITY_TRANSMISSION:
    case SEO_PAGE_TYPES.CITY_ECONOMY:
      crumbs.push({ label: "Carros usados", href: "/v/automap/vehicles" });
      if (config.city) crumbs.push({ label: config.city, href: `/carros-usados-${config.citySlug}` });
      crumbs.push({ label: config.h1, href: config.path });
      break;
    case SEO_PAGE_TYPES.MAKE:
      crumbs.push({ label: "Marcas", href: "/marcas/mazda" });
      crumbs.push({ label: config.make, href: config.path });
      break;
    case SEO_PAGE_TYPES.MAKE_CITY:
      crumbs.push({ label: "Marcas", href: "/marcas/mazda" });
      crumbs.push({ label: config.make, href: `/marcas/${config.makeSlug}` });
      crumbs.push({ label: config.city, href: config.path });
      break;
    case SEO_PAGE_TYPES.MODEL:
      crumbs.push({ label: "Marcas", href: "/marcas/mazda" });
      crumbs.push({ label: config.make, href: `/marcas/${config.makeSlug}` });
      crumbs.push({ label: config.model, href: config.path });
      break;
    case SEO_PAGE_TYPES.MODEL_CITY:
      crumbs.push({ label: "Marcas", href: "/marcas/mazda" });
      crumbs.push({ label: config.make, href: `/marcas/${config.makeSlug}` });
      crumbs.push({ label: config.model, href: `/modelos/${config.makeSlug}/${config.modelSlug}` });
      crumbs.push({ label: config.city, href: config.path });
      break;
    case SEO_PAGE_TYPES.BODY_TYPE:
      crumbs.push({ label: "Tipo de vehículo", href: `/tipo/${config.bodyTypeSlug}` });
      crumbs.push({ label: config.bodyType, href: config.path });
      break;
    case SEO_PAGE_TYPES.BODY_TYPE_CITY:
      crumbs.push({ label: "Tipo de vehículo", href: `/tipo/${config.bodyTypeSlug}` });
      if (config.city) crumbs.push({ label: config.city, href: `/tipo/${config.bodyTypeSlug}/${config.citySlug}` });
      break;
    case SEO_PAGE_TYPES.BUDGET:
      crumbs.push({ label: "Presupuesto", href: `/presupuesto/${config.budgetSlug}` });
      break;
    case SEO_PAGE_TYPES.BUDGET_CITY: {
      const budget = SEO_BUDGET_RANGES.find((b) => b.slug === config.budgetSlug);
      crumbs.push({ label: "Presupuesto", href: "/presupuesto/50-a-80-millones" });
      if (budget) crumbs.push({ label: budget.label, href: `/presupuesto/${budget.slug}` });
      if (config.city) crumbs.push({ label: config.city, href: config.path });
      break;
    }
    case SEO_PAGE_TYPES.DEALER_CITY:
      crumbs.push({ label: "Concesionarios", href: "/v/automap/concesionarios" });
      crumbs.push({ label: config.city, href: config.path });
      break;
    case SEO_PAGE_TYPES.GUIDE:
      crumbs.push({ label: "Guías", href: "/checklist-compra" });
      crumbs.push({ label: config.title, href: config.path });
      break;
    default:
      crumbs.push({ label: config.title, href: config.path });
  }

  return crumbs;
}

export function shouldNoindexSeoPage(config, inventoryCount = null) {
  if (!config) return true;
  if (config.noindex === true) return true;
  if (config.approved === false) return true;

  // Guides are evergreen content — always indexable (M25/M26).
  if (isGuideRichPageType(config.pageType)) {
    return false;
  }

  const hasUniqueContent =
    Boolean(config.introCopy?.length > 80) ||
    (config.faqItems?.length || 0) >= 3 ||
    config.pageType === SEO_PAGE_TYPES.DEALER_CITY;

  if (inventoryCount == null) {
    return config.noindexDefault === true;
  }

  // M26: Make/model city pages with zero inventory should not be indexed (thin combo pages).
  if (
    inventoryCount === 0 &&
    [SEO_PAGE_TYPES.MODEL_CITY, SEO_PAGE_TYPES.MAKE_CITY].includes(config.pageType)
  ) {
    return true;
  }

  // M26: Major Antioquia city pages may stay indexable with unique content even at 0 inventory.
  if (inventoryCount === 0 && config.indexWhenEmpty && hasUniqueContent) {
    return false;
  }

  if (inventoryCount === 0 && !hasUniqueContent) {
    return true;
  }

  if (inventoryCount === 0 && !config.approved) {
    return true;
  }

  const isLowValueCombo =
    [SEO_PAGE_TYPES.BODY_TYPE_CITY, SEO_PAGE_TYPES.BUDGET_CITY].includes(config.pageType) &&
    inventoryCount < SEO_MIN_INVENTORY_TO_INDEX;

  if (isLowValueCombo && !config.indexWhenEmpty) {
    return true;
  }

  return false;
}

export function getSeoFaqItems(config) {
  return config?.faqItems || [];
}

export function buildSeoStructuredData(config, { breadcrumbs, listings = [], inventoryCount = 0 }) {
  const graph = [];

  graph.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: config.h1 || config.title,
    description: config.metaDescription || config.introCopy,
    url: config.canonicalUrl || config.path,
  });

  if (breadcrumbs?.length) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.label,
        item: crumb.href,
      })),
    });
  }

  const faqs = getSeoFaqItems(config);
  if (faqs.length) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    });
  }

  if (listings.length) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      numberOfItems: inventoryCount || listings.length,
      itemListElement: listings.slice(0, 10).map((listing, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `/v/automap/vehicles/${listing.vehicleListingId}`,
        name: formatVehicleTitle(listing),
      })),
    });
  }

  return graph;
}

export function resolveSeoPathFromParams(pathname) {
  return normalizePath(pathname);
}

export function isKnownGuideSlug(slug) {
  const normalized = normalizeSeoSlug(slug);
  const dedicated = new Set(["compra-segura", "checklist-compra-carro-usado"]);
  if (dedicated.has(normalized)) return true;
  return loadSeoPageRegistry().SEO_PAGE_REGISTRY.some(
    (page) => page.pageType === SEO_PAGE_TYPES.GUIDE && page.slug === normalized
  );
}
